import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, isDevMode } from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  withComponentInputBinding,
  withPreloading,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { ExamdojoAuthService, provideAuthTokenInterceptor } from '@examdojo/auth';
import { provideCategoryHeaderInterceptor } from '@examdojo/category/v2';
import { environment, provideEnvironment } from '@examdojo/core/environment';
import { provideFeatureFlag } from '@examdojo/core/feature-flag';
import { DEFAULT_LANGUAGE, provideLocale, SUPPORTED_LANGUAGES } from '@examdojo/core/i18n';
import { provideToastConfig } from '@examdojo/core/toast';
import { provideMarkdownViewer } from '@examdojo/core/ui/markdown-viewer';
import { ExamdojoUserEnvironmentService } from '@examdojo/environment';
import { BackendHealthInterceptor, ErrorHandlerService } from '@examdojo/error-handling';
import { provideExamdojoTranslations, TranslocoHttpLoader } from '@examdojo/i18n';
import { provideLearnHub } from '@examdojo/learn-hub';
import { EXAMDOJO_FEATURE_FLAGS } from '@examdojo/models/feature-flag';
import { PlatformService, provideLayoutBreakpoints } from '@examdojo/platform';
import { QUESTION_ACTIVE_STATUS_STATE, QuestionAttemptEntityQuery } from '@examdojo/question';
import { ACTIVE_ATTEMPT_ID$ } from '@examdojo/solution';
import { IMAGE_BASE_URL } from '@examdojo/study-notes';
import { providePageLayoutSidebarTopPanel } from '@examdojo/ui/page-layout';
import { AuthService } from '@examdojo/user';
import { provideExamdojoUserSettingsQuery } from '@examdojo/user-settings';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgxStripeModule } from 'ngx-stripe';
import { APP_INITIALIZER_PROVIDER } from './app-initializer';
import { LARGE_SCREEN_WIDTH, MEDIUM_SCREEN_WIDTH, SMALL_SCREEN_WIDTH } from './app.model';
import { appRoutes } from './app.routes';
import { provideEntitlements } from './entitlements/provide-entitlement';
import { Features } from './features/features.model';
import { viewTransitionsConfig } from './route-transition';
import { SidebarTopPanelComponent } from './shared/page-layout/sidebar-top-panel/sidebar-top-panel.component';
import { ExamdojoPlatformService } from './shared/platform.service';

export const appConfig: ApplicationConfig = {
  providers: [
    APP_INITIALIZER_PROVIDER,
    {
      provide: QUESTION_ACTIVE_STATUS_STATE,
      useFactory: (s: ExamdojoPlatformService) => s.activeStatusState$,
      deps: [ExamdojoPlatformService],
    },
    {
      provide: ACTIVE_ATTEMPT_ID$,
      useFactory: (query: QuestionAttemptEntityQuery) => query.selectActiveId(),
      deps: [QuestionAttemptEntityQuery],
    },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    provideEnvironment({
      isProductionBuild: environment.production_build,
      name: environment.name,
      releaseVersion: environment.release_version,
      userEnvironmentService: ExamdojoUserEnvironmentService,
    }),
    provideAnimations(),
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding(),
      withViewTransitions(viewTransitionsConfig),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(AngularSvgIconModule.forRoot()),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideToastConfig(),
    importProvidersFrom(NgxStripeModule.forRoot(environment.stripe.key)),
    {
      provide: ExamdojoAuthService,
      useExisting: AuthService,
    },
    AuthService,
    provideTransloco({
      config: {
        availableLangs: SUPPORTED_LANGUAGES,
        defaultLang: DEFAULT_LANGUAGE,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat(),
    provideExamdojoTranslations(),
    provideIonicAngular({
      swipeBackEnabled: false,
      useSetInputAPI: true,
    }),
    provideMarkdownViewer(),
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 500,
        hideDelay: 0,
        touchGestures: 'off',
      } satisfies Partial<MatTooltipDefaultOptions>,
    },
    provideAuthTokenInterceptor(),
    provideCategoryHeaderInterceptor(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BackendHealthInterceptor,
      multi: true,
    },
    provideFeatureFlag(EXAMDOJO_FEATURE_FLAGS),
    provideEntitlements(),
    provideLocale(),
    provideLayoutBreakpoints({
      small: SMALL_SCREEN_WIDTH,
      medium: MEDIUM_SCREEN_WIDTH,
      large: LARGE_SCREEN_WIDTH,
    }),
    provideExamdojoUserSettingsQuery(),
    { provide: IMAGE_BASE_URL, useValue: '/static' },
    { provide: PlatformService, useExisting: ExamdojoPlatformService },
    providePageLayoutSidebarTopPanel(SidebarTopPanelComponent),
    ...provideLearnHub({ learnHubPath: Features.LearnHub, topicPracticePath: Features.TopicPractice }),
  ],
};
